/**
 * MakeCode Arcade 프로젝트 ID (공유·시뮬레이터·embed 공통으로 쓰이는 4세그먼트 형식)
 * 예: S65139-04309-19865-34038, 58434-03828-60826-59319
 */
const ARCADE_PROJECT_ID =
  /\b([A-Za-z0-9]+-[A-Za-z0-9]+-[A-Za-z0-9]+-[A-Za-z0-9]+)\b/;

function buildCanonical(projectId: string): string {
  return `https://arcade.makecode.com/${projectId}`;
}

/**
 * 시뮬레이터 옆 공유 링크, 주소창 URL, `---run?id=...`, embed iframe HTML 등에서
 * arcade.makecode.com 프로젝트 주소 하나로 정규화합니다.
 */
export function normalizeMakeCodeArcadeShareUrl(raw: string): string | null {
  const t = raw.trim();
  if (!t) return null;

  // embed 코드에 포함된 iframe src
  const iframe = t.match(
    /src\s*=\s*["'](https?:\/\/(?:www\.)?arcade\.makecode\.com[^"']+)["']/i
  );
  if (iframe?.[1]) {
    const nested = normalizeMakeCodeArcadeShareUrl(iframe[1]);
    if (nested) return nested;
  }

  let urlStr = t;
  if (!/^[a-z]+:/i.test(urlStr)) {
    urlStr = `https://${urlStr}`;
  }

  try {
    const url = new URL(urlStr);
    const host = url.hostname.replace(/^www\./i, '');
    if (host !== 'arcade.makecode.com') {
      const idOnly = t.match(ARCADE_PROJECT_ID);
      return idOnly ? buildCanonical(idOnly[1]) : null;
    }

    const idParam =
      url.searchParams.get('id') || url.searchParams.get('pubid') || url.searchParams.get('projectid');
    if (idParam) {
      const m = idParam.match(ARCADE_PROJECT_ID);
      if (m) return buildCanonical(m[1]);
      if (/^[A-Za-z0-9-]+$/.test(idParam) && idParam.includes('-')) {
        return buildCanonical(idParam);
      }
    }

    const pathParts = url.pathname.split('/').filter(Boolean);
    for (const part of pathParts) {
      const m = part.match(ARCADE_PROJECT_ID);
      if (m) return buildCanonical(m[1]);
    }

    const inHash = `${url.hash}`.match(ARCADE_PROJECT_ID);
    if (inHash) return buildCanonical(inHash[1]);
  } catch {
    /* fall through */
  }

  const bare = t.match(ARCADE_PROJECT_ID);
  return bare ? buildCanonical(bare[1]) : null;
}

/**
 * 게임 등록(/register)과 같이 넣는 시뮬레이터 iframe HTML에서 `id=` 프로젝트 값 추출.
 * @see GameDetail.tsx renderSimulator
 */
export function extractArcadeRunIdFromSimulatorEmbed(html: string): string | null {
  const m = html.trim().match(/id=([^"&]+)/);
  return m ? m[1] : null;
}

/**
 * 코드 예제·게임 등록과 같은 「공유 링크」 입력값 허용.
 * - arcade.makecode.com/… (프로젝트 ID 정규화 가능 시 canonical)
 * - makecode.com/_… (짧은 공유 링크)
 * - 정규화 실패 시에도 arcade 경로가 있으면 그대로 저장
 */
export function parseCodeExampleShareUrl(raw: string): string | null {
  const t = raw.trim();
  if (!t) return null;

  const normalized = normalizeMakeCodeArcadeShareUrl(t);
  if (normalized) return normalized;

  let url: URL;
  try {
    url = new URL(t.includes('://') ? t : `https://${t}`);
  } catch {
    return null;
  }
  const host = url.hostname.replace(/^www\./i, '');

  if (host === 'makecode.com' && url.pathname.startsWith('/_')) {
    const path = url.pathname.replace(/\/$/, '') || url.pathname;
    return `https://makecode.com${path}${url.search || ''}`;
  }

  if (host === 'arcade.makecode.com' && url.pathname && url.pathname !== '/') {
    return `https://arcade.makecode.com${url.pathname}${url.search || ''}`;
  }

  return null;
}

/** 갤러리용: 예전처럼 arcade.makecode.com 경로만 허용 (정규화 없음) */
export function parseArcadeShareUrlStrict(raw: string): string | null {
  const t = raw.trim();
  if (!t) return null;
  let url: URL;
  try {
    url = new URL(t.includes('://') ? t : `https://${t}`);
  } catch {
    return null;
  }
  const host = url.hostname.replace(/^www\./, '');
  if (host !== 'arcade.makecode.com') return null;
  if (!url.pathname || url.pathname === '/') return null;
  return `https://arcade.makecode.com${url.pathname}${url.search || ''}`;
}
